import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";

const apiUrl = process.env.REACT_APP_API_URL;

export default function CategoryPage() {
  const { name } = useParams();

  const [items, setItems] = useState([]);
  const [fields, setFields] = useState([]);
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const limit = 40;

  /* LOAD FIELDS */
  useEffect(() => {
    axios
      .get(`${apiUrl}/api/categories/${name}/fields`)
      .then((res) => setFields(res.data));
  }, [name]);

  /* LOAD POSTS */
  const fetchPosts = (pageNum, isNewFilter = false) => {
    if (isNewFilter) setLoading(true);
    else setLoadingMore(true);

    axios
      .get(`${apiUrl}/api/categories/${name}/posts`, {
        params: { page: pageNum, limit, ...filters },
      })
      .then((res) => {
        if (res.data.length < limit) setHasMore(false);
        else setHasMore(true);

        setItems((prev) => (isNewFilter ? res.data : [...prev, ...res.data]));
        setLoading(false);
        setLoadingMore(false);
      })
      .catch(() => {
        setLoading(false);
        setLoadingMore(false);
      });
  };

  useEffect(() => {
    setPage(1);
    fetchPosts(1, true);
  }, [name, filters]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPosts(nextPage, false);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <h1 className="text-3xl font-bold capitalize mb-6">{name}</h1>

        {/* FILTER BAR */}
        <div className="bg-white p-4 rounded-2xl shadow mb-6 flex flex-wrap gap-4">
          <select
            onChange={(e) =>
              setFilters((f) => ({ ...f, condition: e.target.value }))
            }
            className="p-2 border rounded-xl"
          >
            <option value="">All Conditions</option>
            <option value="New">New</option>
            <option value="Used">Used</option>
          </select>

          <select
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                priceMin: e.target.value === "low" ? 0 : 1000,
                priceMax: e.target.value === "high" ? "" : 5000,
              }))
            }
            className="p-2 border rounded-xl"
          >
            <option value="">Any Price</option>
            <option value="low">Below 1000</option>
            <option value="mid">1000 – 5000</option>
            <option value="high">5000+</option>
          </select>

          {/* DYNAMIC FILTERS */}
          {fields.map((f) => (
            <select
              key={f.id}
              className="p-2 border rounded-xl"
              onChange={(e) =>
                setFilters((fl) => ({
                  ...fl,
                  [f.name]: e.target.value,
                }))
              }
            >
              <option value="">{`Any ${f.name}`}</option>
              {f.options.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          ))}
        </div>

        {/* PRODUCTS */}
        {loading ? (
          <p className="text-center py-20">Loading...</p>
        ) : items.length === 0 ? (
          <p className="text-center py-20">No items found.</p>
        ) : (
          <section className="mt-10">
            <h2 className="text-xl font-bold mb-4">Similar Items</h2>

            <div className="columns-2 md:columns-3 lg:columns-4 gap-6 space-y-6">
              {items.map((p) => (
                <Link
                  key={p.id}
                  to={`/detail/${p.id}`}
                  className="break-inside-avoid block"
                >
                  <div className="bg-white rounded-2xl shadow hover:shadow-xl transition overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full object-contain"
                      loading="lazy"
                    />

                    <div className="p-3 space-y-1 flex flex-col">
                      <div className="flex items-center justify-between">
                        <p className="text-green-600 font-semibold">
                          {p.price} ETB
                        </p>
                        <p className="text-sm text-gray-500">{p.condition || p.meta?.Condition || p.meta?.condition || ""}</p>
                      </div>
                      <p className="font-medium text-gray-800 leading-tight">
                        {p.title}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {hasMore && (
              <div className="mt-12 flex justify-center pb-12">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="px-8 py-3 bg-white border-2 border-green-600 text-green-600 font-bold rounded-xl hover:bg-green-600 hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                >
                  {loadingMore ? "Loading..." : "Load More Items"}
                </button>
              </div>
            )}
          </section>
        )}
      </div>

      <Footer />
    </div>
  );
}
